import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Badge,
} from 'reactstrap';
import { User, Mail, Phone, Shield, Calendar, CheckCircle, XCircle, Home, CreditCard } from 'react-feather';

const UserViewModel = ({ modal, toggle, userData }) => {
  if (!userData) return null;

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered className="user-view-modal">
      <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">
        <h4 className="mb-0">User Profile Details</h4>
      </ModalHeader>
      <ModalBody className="p-4">
        <div className="user-profile-header mb-4 d-flex align-items-center">
          <div className="avatar-wrapper me-3">
            <div className="avatar-initials bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}>
              {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
            </div>
          </div>
          <div className="user-main-info">
            <h2 className="mb-1">{userData.name}</h2>
            <Badge color={Number(userData.isActive) === 1 ? "success" : "danger"} pill>
              {Number(userData.isActive) === 1 ? <CheckCircle size={14} className="me-1" /> : <XCircle size={14} className="me-1" />}
              {Number(userData.isActive) === 1 ? "Active Account" : "Inactive Account"}
            </Badge>
          </div>
        </div>

        <Row className="g-4">
          <Col md="6">
            <div className="info-card h-100 p-3 border rounded bg-light">
              <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
                <User size={18} className="me-2 text-primary" />
                Contact Information
              </h5>
              <div className="info-item mb-3">
                <small className="text-muted d-block">Email Address</small>
                <div className="d-flex align-items-center mt-1">
                  <Mail size={14} className="me-2 text-muted" />
                  <span className="fw-bold">{userData.email || "--"}</span>
                </div>
              </div>
              <div className="info-item mb-3">
                <small className="text-muted d-block">Phone Number</small>
                <div className="d-flex align-items-center mt-1">
                  <Phone size={14} className="me-2 text-muted" />
                  <span className="fw-bold">{userData.phone || "--"}</span>
                </div>
              </div>
              <div className="info-item">
                <small className="text-muted d-block">Joined Date</small>
                <div className="d-flex align-items-center mt-1">
                  <Calendar size={14} className="me-2 text-muted" />
                  <span className="fw-bold">{userData.createdAt ? new Date(userData.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : "--"}</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md="6">
            <div className="info-card h-100 p-3 border rounded bg-light">
              <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
                <Shield size={18} className="me-2 text-primary" />
                Access Control
              </h5>
              <div className="info-item mb-3">
                <small className="text-muted d-block">System Role</small>
                <div className="mt-1">
                  <Badge color="primary" className="p-2 px-3 fs-6">
                    {userData.role?.roleName || "User"}
                  </Badge>
                </div>
              </div>
              <div className="info-item">
                <small className="text-muted d-block">User ID</small>
                <div className="mt-1 fw-bold text-muted">
                  #{userData.userId}
                </div>
              </div>
            </div>
          </Col>

          <Col xs="12">
            <div className="info-card p-3 border rounded bg-white">
              <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
                <Home size={18} className="me-2 text-primary" />
                Assigned Trusts
              </h5>
              <div className="d-flex flex-wrap gap-2">
                {userData.userRoleDetails?.userTrustLinks?.length > 0 ? (
                  userData.userRoleDetails.userTrustLinks.map((link, index) => (
                    <Badge key={index} color="info" className="p-2 px-3 text-white">
                      {link.trust?.trustName || `Trust ID: ${link.trustId}`}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted small">No trusts assigned</span>
                )}
              </div>
            </div>
          </Col>

          <Col xs="12">
            <div className="info-card p-3 border rounded bg-white">
              <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
                <CreditCard size={18} className="me-2 text-primary" />
                Assigned Bank Accounts
              </h5>
              <div className="row g-2">
                {userData.userRoleDetails?.userBankLinks?.length > 0 ? (
                  userData.userRoleDetails.userBankLinks.map((link, index) => (
                    <Col md="6" key={index}>
                      <div className="bank-item p-2 border rounded d-flex align-items-center bg-light">
                        <div className="bank-icon me-2 text-primary">
                          <CreditCard size={20} />
                        </div>
                        <div>
                          <div className="fw-bold small text-dark" style={{ color: '#212529' }}>{link.bank?.name || "Unknown Bank"}</div>
                          <div className="text-secondary extra-small" style={{ fontSize: '11px', color: '#495057' }}>A/c: {link.bank?.accountNumber || "N/A"}</div>
                        </div>
                      </div>
                    </Col>
                  ))
                ) : (
                  <Col xs="12">
                    <span className="text-muted small">No bank accounts assigned</span>
                  </Col>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </ModalBody>
      <style>{`
        .user-view-modal .modal-content {
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          background-color: #ffffff !important;
          color: #212529 !important;
        }
        .user-view-modal .modal-header {
          background-color: #ffffff !important;
          color: #212529 !important;
        }
        .user-view-modal .modal-header h4 {
          color: #212529 !important;
        }
        .user-view-modal .user-profile-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          margin: -1.5rem -1.5rem 1.5rem -1.5rem;
          padding: 2rem;
          border-bottom: 1px solid #dee2e6;
        }
        .user-view-modal .user-profile-header h2 {
          color: #212529 !important;
        }
        .user-view-modal .avatar-initials {
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
          border: 4px solid white;
        }
        .user-view-modal .info-card {
          transition: transform 0.2s;
          background-color: #ffffff !important;
          border-color: #dee2e6 !important;
        }
        .user-view-modal .info-card.bg-light {
          background-color: #f8f9fa !important;
        }
        .user-view-modal .info-card h5 {
          color: #212529 !important;
          border-bottom-color: #dee2e6 !important;
        }
        .user-view-modal .info-item span {
          color: #212529 !important;
        }
        .user-view-modal .info-item small {
          color: #6c757d !important;
        }
        .user-view-modal .info-card:hover {
          transform: translateY(-2px);
        }
        .user-view-modal .badge {
          font-weight: 500;
          letter-spacing: 0.5px;
        }
        .user-view-modal .bank-item {
          border-left: 3px solid #7366ff !important;
          background-color: #f8f9fa !important;
        }
        .extra-small {
          line-height: 1.2;
        }
      `}</style>
    </Modal>
  );
};

export default UserViewModel;
